import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import type { SearchType } from "@/pages/search/models";
import { useEffect, useMemo, useRef } from "react";

/** Where selecting a suggestion navigates to, as `/search` URL params. */
export interface SearchNav {
  q?: string;
  type?: SearchType;
  /** Opens the results page in previous-orders mode (KAN-51). */
  previous_orders?: true;
}

/**
 * The extra detail a *product* suggestion carries: a thumbnail, what it costs
 * and how many suppliers stock it. Grouped into one optional field rather than
 * three loose ones because they arrive together or not at all - only live
 * typeahead hits have them, never a brand, category, supplier or quick action.
 */
export interface ProductPreview {
  thumbnail: string | null;
  /** Already formatted for display, e.g. `"$12.50"` or `"from $12.50"`. */
  price: string;
  /** How many suppliers list this product. */
  supplierCount: number;
}

export interface Action {
  id: string;
  label: string;
  icon: React.ReactNode;
  description?: string;
  short?: string;
  end?: string;
  isHeader?: boolean;
  /**
   * Explicit navigation target. When set, it overrides the default
   * label/`end`-derived navigation used by live typeahead results.
   */
  navSearch?: SearchNav;
  /** Set on live product results only; see {@link ProductPreview}. */
  product?: ProductPreview;
}

/**
 * The rows a keyboard can land on - everything except the group headers. The
 * highlighted index everywhere in the search UI is an index into *this* list,
 * not into `actions`.
 */
export function getSelectableActions(actions: Action[]): Action[] {
  return actions.filter((action) => !action.isHeader);
}

export interface SearchSuggestionListProps {
  actions: Action[];
  /** Index into {@link getSelectableActions}, or -1 for nothing highlighted. */
  highlightedIndex: number;
  /** Called with a selectable index when the pointer moves onto a row. */
  onHighlight: (index: number) => void;
  onSelect: (action: Action) => void;
  /**
   * Coarse pointer: grow the rows to a 44px touch target. Passed in rather than
   * read here so both surfaces that render this list agree on one answer.
   */
  isTouch: boolean;
}

/**
 * The grouped suggestion rows, shared by the desktop dropdown and the phone
 * search overlay so the two surfaces can never drift in what a suggestion
 * looks like or which row a keystroke lands on.
 *
 * Deliberately *not* animated per row. It used to be: each `<li>` was a
 * `motion.li` with its own enter/exit inside an `AnimatePresence`, so every
 * keystroke that changed the hits faded ~12 old rows out (`popLayout`, i.e.
 * absolutely positioned) while fading ~12 new ones in over the top of them.
 * That overlap *is* the "results double-render for about a second" flicker.
 * The panel's own open/close animation stays with the caller; the rows
 * themselves just swap. See storefront#145 (P3).
 */
export function SearchSuggestionList({
  actions,
  highlightedIndex,
  onHighlight,
  onSelect,
  isTouch
}: SearchSuggestionListProps) {
  const listItemsRef = useRef<(HTMLLIElement | null)[]>([]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (highlightedIndex >= 0 && listItemsRef.current[highlightedIndex]) {
      listItemsRef.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth"
      });
    }
  }, [highlightedIndex]);

  // Pair each action with its index among the selectable ones, counted in
  // render order, so neither this component nor the keyboard handler has to
  // search the list per row.
  const rows = useMemo(() => {
    let nextSelectable = 0;
    return actions.map((action) => ({
      action,
      selectableIndex: action.isHeader ? -1 : nextSelectable++
    }));
  }, [actions]);

  return (
    <ul>
      {rows.map(({ action, selectableIndex }) => {
        if (action.isHeader) {
          return (
            <li
              key={action.id}
              className="flex items-center gap-2 px-3 pb-1 pt-3 first:pt-2"
            >
              <span className="text-gray-400">{action.icon}</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {action.label}
              </span>
            </li>
          );
        }

        return (
          <li
            key={action.id}
            ref={(el) => {
              listItemsRef.current[selectableIndex] = el;
            }}
            className={`flex cursor-pointer items-center justify-between gap-2 rounded-md px-3 transition-colors ${
              // A finger needs the 44px target; a mouse doesn't, and paying for
              // it on desktop would show fewer suggestions per screen.
              isTouch ? "min-h-[44px] py-3" : "py-2"
            } ${
              selectableIndex === highlightedIndex
                ? "bg-blue-100 dark:bg-blue-900"
                : "hover:bg-gray-200 dark:hover:bg-zinc-900"
            }`}
            onClick={() => onSelect(action)}
            onMouseEnter={() => onHighlight(selectableIndex)}
          >
            {action.product ? (
              <ProductSuggestion action={action} product={action.product} />
            ) : (
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-gray-500">{action.icon}</span>
                <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {action.label}
                </span>
                {action.description && (
                  <span className="truncate text-xs text-gray-400">
                    {action.description}
                  </span>
                )}
              </div>
            )}
            <span className="flex-shrink-0 text-xs text-gray-400">
              {action.end}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A product row: thumbnail, title, then price and supplier count on a second
 * line. `min-w-0` at every level of the flex chain is load-bearing - without
 * it the `truncate` title reports its full width as the row's minimum and the
 * row pushes the dropdown wider than the phone viewport.
 */
function ProductSuggestion({
  action,
  product
}: {
  action: Action;
  product: ProductPreview;
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <ProductThumbnailImage
        src={product.thumbnail}
        // The title sits right beside it, so announcing the image too would
        // just read the product name twice.
        alt=""
        // A floating full-size preview belongs on a results card, not over an
        // autocomplete list the pointer is passing through - and it is dead
        // weight on the touch devices this row was enriched for.
        enablePreview={false}
        className="h-10 w-10 flex-shrink-0 rounded object-contain"
        fallback={
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-gray-100 dark:bg-zinc-900">
            {action.icon}
          </div>
        }
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {action.label}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {product.price}
          </span>
          <span aria-hidden="true">·</span>
          <span className="truncate">
            {product.supplierCount}{" "}
            {product.supplierCount === 1 ? "supplier" : "suppliers"}
          </span>
        </div>
      </div>
    </div>
  );
}
